import Services from "../services/Services";
import Empresa from "./Empresa";
import Perfil from "./Perfil";
import DAO from "../DAO/DAO";
import StringHelper from "../helpers/StringHelper";
import DateHelper from "../helpers/DateHelper";
import moment from "moment";

export default class Usuario {
	pk;
	cpf;
	email;
	nome;
	dataNascimento;
	telefone;
	foto;
	fotoThumb;
	empresa;
	perfil;
	cep;
	logradouro;
	numero;
	complemento;
	bairro;
	cidade;
	estado;
	token;
	validadeToken;
	senhaTemporaria;
	colaborador;
	colaboradores;
	gerenciados;
	isSuperUser;
	isStaff;
	criadoEm;
	bloqueado;

	constructor({
		pk,
		cpf,
		email,
		nome,
		dataNascimento,
		telefone,
		foto,
		fotoThumb,
		empresa,
		perfil,
		cep,
		logradouro,
		numero,
		complemento,
		bairro,
		cidade,
		estado,
		token,
		validadeToken,
		senhaTemporaria,
		colaborador,
		colaboradores,
		gerenciados,
		isSuperUser,
		isStaff,
		criadoEm,
		bloqueado
	}) {
		this.pk = pk;
		this.cpf = cpf;
		this.email = email;
		this.nome = nome;
		this.dataNascimento = dataNascimento;
		this.telefone = telefone;
		this.foto = foto;
		this.fotoThumb = fotoThumb;
		this.empresa = Empresa.fromMap(empresa);
		this.perfil = perfil instanceof Perfil ? perfil : (perfil ? Perfil.fromMap(perfil) : null);
		this.cep = cep;
		this.logradouro = logradouro;
		this.numero = numero;
		this.complemento = complemento;
		this.bairro = bairro;
		this.cidade = cidade;
		this.estado = estado;
		this.token = token;
		this.validadeToken = validadeToken;
		this.senhaTemporaria = senhaTemporaria;
		this.colaborador = colaborador;
		this.colaboradores = colaboradores;
		this.gerenciados = gerenciados;
		this.isSuperUser = isSuperUser;
		this.isStaff = isStaff;
		this.criadoEm = criadoEm;
		this.bloqueado = bloqueado;
	}

	static getUsuarioAutenticado() {
		return new Usuario(DAO.user.get());
	}

	static async getUsuario(usuarioPk) {
		let usuario = await Services.UsuarioService.usuario(usuarioPk);

		if (usuario) {
			return Usuario.fromMap(usuario);
		}

		return null;
	}

	static async getUsuarioPorEmpresa(empresaPk, usuarioPk) {
		let usuario = await Services.UsuarioService.usuarioPorEmpresa(empresaPk, usuarioPk);

		if (usuario) {
			return Usuario.fromMap(usuario);
		}

		return null;
	}

	static async getUsuarios(params) {
		const usuarioResponse = await Services.UsuarioService.usuarios(params);

		if (usuarioResponse.results) {
			return {
				usuarios: Usuario.fromListMap(usuarioResponse.results),
				totalUsuarios: usuarioResponse.count
			};
		}

		return [];
	}

	static async getUsuariosPorEmpresa(empresaPk, params) {
		const usuarioResponse = await Services.UsuarioService.usuariosPorEmpresa(
			empresaPk,
			params
		);

		if (usuarioResponse.results) {
			return {
				usuarios: Usuario.fromListMap(usuarioResponse.results),
				totalUsuarios: usuarioResponse.count
			};
		}

		return [];
	}

	static async getUsuariosGerenciados(empresaPk, params) {
		const usuarioResponse = await Services.UsuarioService.usuariosGerenciados(
			empresaPk,
			params
		);

		if (usuarioResponse.results) {
			return {
				usuarios: Usuario.fromListMap(usuarioResponse.results),
				totalUsuarios: usuarioResponse.count
			};
		}

		return [];
	}

	static async getUsuariosSupervisores(empresaPk, params) {
		const usuarioResponse = await Services.UsuarioService.usuariosSupervisores(
			empresaPk,
			params
		);

		if (usuarioResponse.results) {
			return {
				usuarios: Usuario.fromListMap(usuarioResponse.results),
				totalUsuarios: usuarioResponse.count
			};
		}

		return [];
	}

	static async getUsuarioPorCpf(cpfUsuario) {
		const cpf = StringHelper.alphanumericOnly(cpfUsuario);
		const usuario = await Services.UsuarioService.getUsuarioPorCpf({ cpf });
		return usuario;
	}

	static async criar(usuario, empresaPk) {
		const data = Usuario.toFormData(usuario);

		if (empresaPk) {
			await Services.UsuarioService.criarUsuarioPorEmpresa(
				empresaPk,
				data
			);
		} else {
			await Services.UsuarioService.criarUsuario(data);
		}
	}

	static async editar(usuario, empresaPk) {
		const data = Usuario.toFormData(usuario);

		if (empresaPk) {
			await Services.UsuarioService.editarUsuarioPorEmpresa(
				empresaPk,
				usuario.pk,
				data
			);
		} else {
			await Services.UsuarioService.editarUsuario(usuario.pk, data);
		}
	}

	static async mudarSenha(senha_atual, nova_senha) {
		const mudarResponse = await Services.UsuarioService.mudarSenha(
			{ senha_atual, nova_senha }
		);
		return mudarResponse;
	}

	static validarSenhas(senhaAtual, novaSenha, confirmarSenha) {
		if (!senhaAtual) return null;

		if (!novaSenha && !confirmarSenha) {
			throw {
				fields_errors: [
					{
						field: "nova_senha",
						errors: [
							{
								message: "Este campo é obrigatório.",
								code: "required"
							}
						]
					},
					{
						field: "confirmar_senha",
						errors: [
							{
								message: "Este campo é obrigatório.",
								code: "required"
							}
						]
					}
				]
			};
		} else if (!novaSenha) {
			throw {
				fields_errors: [
					{
						field: "nova_senha",
						errors: [
							{
								message: "Este campo é obrigatório.",
								code: "required"
							}
						]
					}
				]
			};
		} else if (!confirmarSenha) {
			throw {
				fields_errors: [
					{
						field: "confirmar_senha",
						errors: [
							{
								message: "Este campo é obrigatório.",
								code: "required"
							}
						]
					}
				]
			};
		}

		if (novaSenha !== confirmarSenha) {
			throw {
				error: { message: "As senhas estão diferentes." }
			};
		}
	}

	static validarObrigatoriedadeConfirmacaoSenha(error, senhaAtual, confirmarSenha) {
		if (senhaAtual) return null;

		if (error && error.fields_errors) {
			if (!confirmarSenha) {
				error.fields_errors.push(
					{field: "confirmar_senha", errors: [{message: "Este campo é obrigatório.", code: "required"}]}
				)
			}
		}
	}

	static messageErrorMudarSenha(error) {
		try {
			error.fields_errors.forEach(fieldErro => {
				const fields_errors = [];

				fieldErro.errors.forEach((erro, index) => {
					if ("password" in erro) {
						erro["password"].forEach(messageErro => {
							fields_errors.push({
								message: messageErro
							});
						});

						fieldErro.errors.splice(index, 1);
					}
				});

				fieldErro.errors = [...fieldErro.errors, ...fields_errors];
			});
		} catch (error) {
			return null;
		}
	}

	static toFormData(usuario) {
		if (!usuario) return usuario;

		usuario = Usuario.toMap(usuario);
		delete usuario.fotoThumb;

		const formData = new FormData();

		Object.keys(usuario).forEach(key => {
			switch (key) {
				case "empresa":
					if (!usuario[key].pk) return;
					formData.append(key, usuario[key].pk);
					break;

				case "perfil":
					if (!usuario[key].pk) return;
					formData.append(key, usuario[key].pk);
					break;

				case "gerenciados":
					if (!usuario[key] || usuario.perfil.isAdmin) return;
					usuario[key].forEach(gerenciado => {
						formData.append(key, gerenciado.pk);
					});
					break;

				case "foto":
					if (usuario[key] === null) {
						formData.append(key, "");
					}
					if (!(usuario[key] instanceof File)) return;
					formData.append(key, usuario[key]);
					break;

				case "bloqueado":
					if (typeof usuario[key] !== "boolean") return;
					formData.append(key, usuario[key]);
					break;

				case "cpf":
					if (!usuario[key]) return;
					formData.append(
						key,
						StringHelper.alphanumericOnly(usuario[key])
					);
					break;

				case "telefone":
					if (!usuario[key]) return;
					formData.append(
						key,
						StringHelper.alphanumericOnly(usuario[key])
					);
					break;

				case "cep":
					if (!usuario[key]) return;
					formData.append(
						key,
						StringHelper.alphanumericOnly(usuario[key])
					);
					break;

				case "data_nascimento":
					if (!usuario[key]) return;
					formData.append(
						key,
						DateHelper.convertDate(
							usuario[key],
							"DD/MM/YYYY",
							"YYYY-MM-DD"
						)
					);
					break;

				default:
					if (!usuario[key]) return;
					formData.append(key, usuario[key]);
					break;
			}
		});

		return formData;
	}

	static toMap(usuario) {
		usuario = {
			...usuario,
			data_nascimento: usuario.dataNascimento
		};

		delete usuario.dataNascimento;

		return usuario;
	}

	static fromMap(map) {
		if (!map) {
			return map;
		}

		Object.keys(map).forEach(key => {
			if (map[key] === null || map[key] === undefined) {
				delete map[key];
			}
		});

		let dataNascimento = moment(map.data_nascimento);

		if (dataNascimento.isValid()) {
			dataNascimento = dataNascimento.toDate();
		} else {
			dataNascimento = null;
		}

		const bloqueado = map.bloqueado === true ? "Bloqueado" : "Desbloqueado";

		const { perfil: perfilRaw, ...restoMap } = map;

		const usuarioData = {
			...restoMap,
			dataNascimento: dataNascimento,
			fotoThumb: map.foto_thumb,
			validadeToken: map.validade_token,
			senhaTemporaria: map.senha_temporaria,
			isSuperUser: map.is_superuser,
			isStaff: map.is_staff,
			criadoEm: map.created_at,
			bloqueado,
			perfil: perfilRaw ? Perfil.fromMap(perfilRaw) : null
		};

		return new Usuario(usuarioData);
	}

	static fromListMap(mapList) {
		return mapList.map(map => {
			return Usuario.fromMap(map);
		});
	}
}
